/* eslint-disable import/no-cycle */
import axios from 'axios'
import moment from 'moment'
// eslint-disable-next-line import/no-cycle
import { Message } from '@qs/ui'
import { Loading } from 'element-ui'
import store from '../store'
import traceIdPlugin from './request/plugins/trace-id-plugin'
import trimPlugin from './request/plugins/trim-plugin'
import privacyPlugin from './request/plugins/privacy-plugin'
import sentryPlugin from './request/plugins/sentry-plugin'

import {
  getTenantId,
  getToken, getTokenExpires, gotoNewWindow,
} from '@/utils/auth'
import { generateSignWithParams } from '@/utils/encrypte'
import { getIsReject, getRequestJudge, setIsReject } from './multiTenantUtil'

let preRefreshToken = ''

/**
 * 是否执行刷新 token
 * 当前服务端定义的 token 有效期为 2 小时
 * 前端做检查 如果 token 有效期 - 当前时间 < 5 分钟则执行次刷新
 */
const needRefreshToken = () => {
  const token = getToken()
  const expires = getTokenExpires()
  const currentTime = moment().valueOf()

  if ((preRefreshToken !== token) && expires > 0 && ((expires - currentTime) < (5 * 60 * 1000))) {
    preRefreshToken = token
    store.dispatch('RefreshToken')
  }
}

// 创建axios实例
const service = axios.create({
  timeout: 60000 * 3, // 请求超时时间
})

// request拦截器
service.interceptors.request.use(
  async (config) => {
    // POST/PUT/PATCH 请求没有 data 时，自动设置空对象（服务端要求）
    const method = (config.method || 'get').toLowerCase()
    if (['post', 'put', 'patch'].includes(method) && !config.data) {
      config.data = {}
    }

    config.headers._admin_ts = moment().unix() // 时间戳(秒)  long
    config.headers._admin_eid = getTenantId() // 商户ID  long

    const { url, data, signParams } = config
    let signResult = {}

    // 如果配置了核心参数，使用新签名方式
    if (signParams && Array.isArray(signParams) && signParams.length > 0) {
      signResult = await generateSignWithParams(url, data, signParams)
      const { ts, sign, signatureText } = signResult
      if (ts && sign) {
        config.headers._yy_ts = ts
        config.headers._yy_sign = sign
        config.headers._yy_sign_text = encodeURIComponent(signatureText)
      }
    }

    const {
      token,
    } = store.getters

    if (token) {
      config.headers._admin_tk = token // 让每个请求携带自定义token 请根据实际情况自行修改
    }
    // 是否需要重新跳转到welcome页面
    if (getRequestJudge(config.url)) {
      if (!getIsReject()) {
        Loading.service()
        setTimeout(() => {
          gotoNewWindow()
        }, 2000)
        Message({
          message: '账户租户权限更新，返回首页。',
          type: 'error',
          duration: 2 * 1000,
        })
      }
      setIsReject()
      return Promise.reject()
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// response 拦截器
service.interceptors.response.use(
  (response) => {
    const res = response.data
    if (response.config && response.config.isExport) {
      return response.data
    }
    if (!res || res.code !== 1) {
      // 如果有要特殊处理的错误信息的时候
      let { bizCode } = response.config
      if (bizCode && !Array.isArray(bizCode)) {
        bizCode = [bizCode]
      }
      if (bizCode?.includes(res.code)) {
        return Promise.reject(res) // TODO: 这个设计不好，既然是业务代码，就不应该视为异常，应该直接return 而不是reject，后续要改掉
      }
      // 无权限不提示
      if (res.code === 20010) {
        return res
      }

      // 用户未登录 退出登录
      if (res.code === 20004) {
        store.dispatch('FedLogOut').then(() => {
          location.reload()
        })
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        return new Promise(() => {})
      }

      // 批量上架商品 Session 过期
      if (res.code === 80000) {
        store.commit('batchPublish/SET_SESSION_EXPIRED', true)
        return res
      }

      if (res.msg) {
        Message({ message: res.msg, type: 'error', duration: 5000 })
      }

      // 导入失败 弹窗提示
      if (res.code === 23023) {
        return res
      }

      return res
    } else {
      needRefreshToken()
      return response.data
    }
  },
  (error) => {
    // 暂时注释埋点上报
    if (error.message && error.message.includes('timeout')) {
      return Promise.reject(error)
    }
    /**
     * 转化响应类型，优化cancel提示
     * Axios 1.x 使用 error.code === 'ERR_CANCELED' 替代 axios.isCancel
     */
    if (error.code === 'ERR_CANCELED') {
      const err = new Error('请勿重复请求')
      err.code = 90000
      return Promise.reject(err)
    }
    Message({ message: error.message, type: 'error', duration: 5000 })
    return Promise.reject(error)
  },
)

// sentry 请求上报插件（必须最先注册 = 最后执行，确保捕获到 traceId 等完整数据）
sentryPlugin(service)
// 字符串自动trim插件
trimPlugin(service)
// 自动注入 traceId 插件
traceIdPlugin(service)
// 响应头隐私状态同步插件
privacyPlugin(service)
export default service
